import React from 'react'
import { Container } from 'typescript-ioc'
import { Feed } from '@pushly/models/lib/structs/feeds/feed'
import { antdStaticApi } from 'src/providers/antd-app-provider/antd-static-api'
import { FeedService } from 'src/services/feed.service'
// `import type` specifically: AQE's feeds `types` module also imports class-transformer
// and a models struct for the unrelated GetFeedNotificationWithStatsResponse class, so a
// type-only import erases those at runtime.
import type {
    FeedJourneyReference,
    FeedJourneyReferenceEntry,
    FeedJourneyReferenceStep,
} from '@pushly/aqe/lib/components/feeds/types'

import './feed-reference-guards.scss'

/**
 * Archive guard + disable warning for feeds referenced by journeys.
 *
 * Both warnings read the same "journeys-using-feed" reference data that powers the
 * feed detail "Used in Journeys" card and the feed list "In N journeys" link,
 * sourced from the backend endpoint
 * `GET /v4/domains/{domainId}/feeds/{feedId}/journey-associations` via
 * {@link getFeedJourneyReferences}. That seam degrades gracefully: on any failure
 * (or when no journey references the feed) it resolves `undefined`, so these
 * helpers show NO warning and proceed normally.
 */

/**
 * The reference shapes are owned by AQE (`FeedDetailsView` accepts
 * `journeyReferences?: FeedJourneyReferenceEntry`), so they are consumed from there
 * rather than re-declared as a structural twin. Re-exported below so the existing
 * `./feed-reference-guards` import paths keep resolving.
 */
export type { FeedJourneyReference, FeedJourneyReferenceEntry, FeedJourneyReferenceStep }

/**
 * Pure mapper from the raw journeys-using-feed payload to a
 * {@link FeedJourneyReferenceEntry}.
 *
 * Returns `undefined` for anything not shaped like `{ count: number, journeys: [] }`
 * For a well-formed payload it mirrors `count` and each journey's
 * `id` / `name` / `status` / `steps` (and each step's `id` / `label`).
 *
 * The payload is external, so every field backing a NON-optional property is
 * validated: a journey missing a numeric `id` or a string `name` — or a step missing
 * a numeric `id` — rejects the WHOLE payload (`undefined`), the same way a non-object
 * journey entry already does. Those fields are what the UI renders and keys/routes
 * by, so an entry without them is not representable and must never be typed as though
 * it were.
 *
 * Optional properties (`status`, `label`, `steps`) are taken only when present with
 * the declared type and are otherwise left ABSENT rather than replaced with a
 * placeholder — absent is already a first-class state in both
 * the type and the renderers, so a decorative field arriving as `null` must not
 * discard the whole reference set (and with it the archive warning).
 *
 * Side-effect free so it can be unit-tested independently of any HTTP call.
 */
export function mapFeedJourneyReferenceEntry(data: unknown): FeedJourneyReferenceEntry | undefined {
    if (typeof data !== 'object' || data === null) return undefined

    const record = data as { count?: unknown; journeys?: unknown }
    if (typeof record.count !== 'number' || !Array.isArray(record.journeys)) return undefined

    const journeys: FeedJourneyReference[] = []
    for (const rawJourney of record.journeys) {
        if (typeof rawJourney !== 'object' || rawJourney === null) return undefined

        const source = rawJourney as { id?: unknown; name?: unknown; status?: unknown; steps?: unknown }
        if (typeof source.id !== 'number' || typeof source.name !== 'string') return undefined

        const journey: FeedJourneyReference = { id: source.id, name: source.name }

        if (typeof source.status === 'string') {
            journey.status = source.status
        }

        if (Array.isArray(source.steps)) {
            const steps: FeedJourneyReferenceStep[] = []

            for (const rawStep of source.steps) {
                if (typeof rawStep !== 'object' || rawStep === null) return undefined

                const stepSource = rawStep as { id?: unknown; label?: unknown }
                if (typeof stepSource.id !== 'number') return undefined

                const step: FeedJourneyReferenceStep = { id: stepSource.id }
                if (typeof stepSource.label === 'string') {
                    step.label = stepSource.label
                }

                steps.push(step)
            }

            journey.steps = steps
        }

        journeys.push(journey)
    }

    return { count: record.count, journeys }
}

/**
 * Resolve the journeys that reference a feed.
 *
 * Issues exactly one request to the References_Endpoint scoped to the given
 * `domainId` / `feedId` via {@link FeedService.fetchFeedJourneyReferences},
 * then maps the payload with {@link mapFeedJourneyReferenceEntry}. Resolves
 * `undefined` — never throwing — when the request fails, returns a non-success
 * status, or returns a payload that cannot be mapped, so the
 * archive/disable guards degrade gracefully and the "Used in Journeys" card is omitted.
 */
export async function getFeedJourneyReferences(
    domainId: number,
    feedId: number,
): Promise<FeedJourneyReferenceEntry | undefined> {
    try {
        const feedSvc = Container.get(FeedService)
        const res = await feedSvc.fetchFeedJourneyReferences(domainId, feedId)
        if (!res.ok) return undefined
        return mapFeedJourneyReferenceEntry(res.data)
    } catch {
        return undefined
    }
}

/**
 * True when the reference data indicates at least one referencing journey.
 *
 * Typed as a predicate so the guards below narrow `references` to a defined entry
 * after the early return, instead of re-asserting it non-null.
 */
export function hasJourneyReferences(references?: FeedJourneyReferenceEntry): references is FeedJourneyReferenceEntry {
    return !!references && references.count > 0 && references.journeys.length > 0
}

/** Renders the list of referencing journeys with their status inside a modal body. */
function renderReferencingJourneys(references: FeedJourneyReferenceEntry): React.ReactNode {
    return (
        <ul className="feed-referencing-journeys">
            {references.journeys.map((journey) => (
                <li key={journey.id}>
                    <span className="journey-name">{journey.name}</span>
                    {journey.status && <span className="journey-status"> — {journey.status}</span>}
                </li>
            ))}
        </ul>
    )
}

interface ArchiveGuardArgs {
    feed: Feed
    references?: FeedJourneyReferenceEntry
    /** Performs the actual archive once the user confirms (or when unreferenced). */
    onConfirm: () => void | Promise<void>
}

/**
 * Archive guard. When the feed is referenced by one or more active
 * journeys, shows a blocking confirmation modal titled "Archive this feed?" with the
 * referencing journeys, an irreversible-action notice, and a danger "Archive Feed"
 * action. When the feed is unreferenced (or reference data is unavailable) it shows
 * NO warning and archives directly (graceful degradation).
 */
export function confirmArchiveReferencedFeed({ feed, references, onConfirm }: ArchiveGuardArgs): void {
    if (!hasJourneyReferences(references)) {
        void onConfirm()
        return
    }

    const count = references.count
    const journeyText = count === 1 ? 'active journey' : 'active journeys'

    antdStaticApi.modal.confirm({
        title: 'Archive this feed?',
        okText: 'Archive Feed',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        content: (
            <div className="feed-archive-guard-modal">
                <p>
                    <b>{feed.name}</b> is used in {count} {journeyText}. Archiving it will stop those steps from sending
                    feed articles.
                </p>
                {renderReferencingJourneys(references)}
                <p>This cannot be undone.</p>
            </div>
        ),
        onOk: () => onConfirm(),
    })
}

interface DisableGuardArgs {
    feed: Feed
    references?: FeedJourneyReferenceEntry
    /** Proceeds with the disable when the user confirms (or when unreferenced). */
    onConfirm: () => void | Promise<void>
    /** Optional abort hook when the user cancels the informational warning. */
    onCancel?: () => void
}

/**
 * Disable warning. When disabling a feed (setting `enabled = false`)
 * that is referenced by journeys, shows a NON-BLOCKING informational confirm/cancel
 * noting the feed is used in N journeys and that disabling it does not affect journey
 * sends (per the ADR: a disabled feed does not stop journey sends). Confirm proceeds,
 * cancel aborts — it never blocks. When unreferenced (or reference data is
 * unavailable) it proceeds with no warning.
 */
export function confirmDisableReferencedFeed({ feed, references, onConfirm, onCancel }: DisableGuardArgs): void {
    if (!hasJourneyReferences(references)) {
        void onConfirm()
        return
    }

    const count = references.count
    const journeyText = count === 1 ? 'journey' : 'journeys'

    antdStaticApi.modal.confirm({
        title: 'Disable this feed?',
        okText: 'Disable Feed',
        cancelText: 'Cancel',
        content: (
            <div className="feed-disable-guard-modal">
                <p>
                    <b>{feed.name}</b> is used in {count} {journeyText}. Disabling the feed does not affect journey
                    sends — those steps will continue sending feed articles.
                </p>
            </div>
        ),
        onOk: () => onConfirm(),
        onCancel: () => onCancel?.(),
    })
}
